/*
 * Healix Colours — storefront data library (single source of truth).
 *
 * Replaces the external `swatch.d2c.in` provider with Healix's first-party
 * Colours API. Every storefront surface calls THIS library instead of hardcoding
 * the provider URL and re-implementing fetch+parse.
 *
 * Returns the SAME shapes the theme already consumed, so renderers are untouched:
 *   - colorProductSiblings(handles) -> { result: { core:[entry], le:[entry] } }
 *       entry = { handle (family base), variant:{ handle, color, color_code, price } }
 *   - linkedProducts(handle[, {withColors,enrich}]) -> { wearit:[item], complete:[item] }
 *
 * SPEED & RESILIENCE:
 *  - COALESCE: all sibling lookups made within a short window collapse into ONE
 *    batched request (chunked ≤100) — a whole page makes ~1–3 calls, not dozens.
 *  - PERSIST: per-handle siblings + linked results are cached in localStorage
 *    (TTL), so navigations and return visits render instantly with ZERO network
 *    and the API barely gets hit (vital given a low Lambda concurrency limit).
 *  - PREFETCH: hovering/touching a product link warms that product's siblings +
 *    linked data, so the PDP's swatches and "wear it with" are ready on click.
 *  - RETRY: transient failures (503 under load) retry with backoff; on final
 *    failure the surface degrades gracefully (no swatches) rather than erroring.
 *
 * Configure the API base in ONE place (or via window.HEALIX_COLORS_API_BASE).
 */
(function () {
  'use strict';

  var BASE = (window.HEALIX_COLORS_API_BASE ||
    'https://mckj5amy32.execute-api.us-east-1.amazonaws.com/v1').replace(/\/+$/, '');
  var BATCH_MAX = 100;          // POST /v1/siblings:batch hard cap
  var FLUSH_MS = 60;            // coalescing window
  var MAX_TRIES = 4;            // batch retry attempts (transient 503s under load)
  var CHUNK_CONCURRENCY = 1;    // serialize batch chunks per page — a whole
                                // page's sibling load holds ONE Lambda slot at a
                                // time (max headroom under the low account
                                // concurrency cap until the quota increase lands)
  var LS_PREFIX = 'hxc.v1.';   // localStorage namespace (bump suffix to bust)
  var TTL_MS = 30 * 60 * 1000; // 30 min — colour data changes rarely
  var PREFETCH_HOVER_MS = 90;   // hover-intent delay before prefetching

  function fetchJSON(url, opts) {
    return fetch(url, opts).then(function (r) {
      if (!r.ok) throw new Error('HTTP ' + r.status + ' for ' + url);
      return r.json();
    });
  }

  function chunk(arr, n) {
    var out = [];
    for (var i = 0; i < arr.length; i += n) out.push(arr.slice(i, i + n));
    return out;
  }

  // Run async tasks with bounded concurrency (resolves when all complete).
  function runLimited(items, limit, fn) {
    return new Promise(function (resolve) {
      var i = 0, active = 0;
      function next() {
        if (i >= items.length && active === 0) return resolve();
        while (active < limit && i < items.length) {
          active++;
          fn(items[i++]).then(function () { active--; next(); }, function () { active--; next(); });
        }
      }
      next();
    });
  }

  // ── Persistent cache (localStorage, TTL) — degrades to in-memory if blocked ──
  function lsGet(key) {
    try {
      var raw = window.localStorage.getItem(LS_PREFIX + key);
      if (!raw) return null;
      var o = JSON.parse(raw);
      if (!o || (Date.now() - o.t) > TTL_MS) { window.localStorage.removeItem(LS_PREFIX + key); return null; }
      return o.d;
    } catch (e) { return null; }
  }
  function lsSet(key, d) {
    try {
      window.localStorage.setItem(LS_PREFIX + key, JSON.stringify({ t: Date.now(), d: d }));
    } catch (e) {
      // Quota or private mode — drop our old keys once, then give up silently.
      try {
        for (var i = window.localStorage.length - 1; i >= 0; i--) {
          var k = window.localStorage.key(i);
          if (k && k.indexOf(LS_PREFIX) === 0) window.localStorage.removeItem(k);
        }
        window.localStorage.setItem(LS_PREFIX + key, JSON.stringify({ t: Date.now(), d: d }));
      } catch (e2) { /* in-memory only */ }
    }
  }

  // colourway handle -> family/base handle (strip the colour slug suffix).
  function baseHandle(handle, color) {
    var h = String(handle || '');
    var c = String(color || '').trim();
    if (c) {
      var suffix = '-' + c;
      if (h.length > suffix.length && h.slice(-suffix.length).toLowerCase() === suffix.toLowerCase()) {
        return h.slice(0, -suffix.length);
      }
    }
    return h.replace(/-[^-]+$/, '');
  }

  function toLegacyEntry(it) {
    var vh = String(it.handle || '');
    return {
      handle: baseHandle(vh, it.color),
      variant: { handle: vh, color: it.color, color_code: it.hex, price: it.price }
    };
  }

  // ── Coalesced + cached + retried per-handle siblings fetch ────────────────
  var _cache = {};     // handle -> { core:[newEntry], limited:[newEntry] } (in-memory hot path)
  var _pending = {};   // handle -> true (queued for the next flush)
  var _waiters = [];   // [{ resolve }] resolved once the current flush completes
  var _timer = null;

  // In-memory, else fresh localStorage, else null (needs fetch).
  function getSib(h) {
    if (_cache[h]) return _cache[h];
    var d = lsGet('sib.' + h);
    if (d) { _cache[h] = d; return d; }
    return null;
  }

  function postBatchWithRetry(handles, tries) {
    tries = tries || MAX_TRIES;
    return fetchJSON(BASE + '/siblings:batch', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ handles: handles })
    })
      .then(function (d) { return d.siblings || {}; })
      .catch(function () {
        if (tries > 1) {
          var wait = (MAX_TRIES - tries + 1) * 300 + Math.floor(Math.random() * 250);
          return new Promise(function (res) { setTimeout(res, wait); })
            .then(function () { return postBatchWithRetry(handles, tries - 1); });
        }
        return null; // give up after retries — caller degrades to "no swatches"
      });
  }

  function flush() {
    _timer = null;
    var waiters = _waiters; _waiters = [];
    var toFetch = Object.keys(_pending); _pending = {};
    function runChunk(c) {
      return postBatchWithRetry(c).then(function (sib) {
        if (sib) c.forEach(function (h) {
          var d = sib[h] || { core: [], limited: [] };
          _cache[h] = d;
          lsSet('sib.' + h, d);
        });
        // on null (failed) leave uncached so a later call may retry
      });
    }
    var work = toFetch.length
      ? runLimited(chunk(toFetch, BATCH_MAX), CHUNK_CONCURRENCY, runChunk)
      : Promise.resolve();
    work.then(function () { waiters.forEach(function (w) { w.resolve(); }); });
  }

  // Ensure handles are cached; coalesces concurrent callers into one batch.
  function ensureSiblings(handles) {
    var uncached = handles.filter(function (h) { return h && !getSib(h); });
    if (!uncached.length) return Promise.resolve();
    return new Promise(function (resolve) {
      uncached.forEach(function (h) { _pending[h] = true; });
      _waiters.push({ resolve: resolve });
      if (!_timer) _timer = setTimeout(flush, FLUSH_MS);
    });
  }

  function legacyUnion(handles) {
    var core = [], le = [], seenC = {}, seenL = {};
    handles.forEach(function (h) {
      var sib = getSib(h);
      if (!sib) return;
      (sib.core || []).forEach(function (it) {
        if (it && !seenC[it.handle]) { seenC[it.handle] = true; core.push(toLegacyEntry(it)); }
      });
      (sib.limited || []).forEach(function (it) {
        if (it && !seenL[it.handle]) { seenL[it.handle] = true; le.push(toLegacyEntry(it)); }
      });
    });
    return { result: { core: core, le: le } };
  }

  function colorProductSiblings(handles) {
    var list = (Array.isArray(handles) ? handles : [handles])
      .map(function (h) { return String(h || '').trim(); })
      .filter(Boolean);
    if (!list.length) return Promise.resolve({ result: { core: [], le: [] } });
    return ensureSiblings(list).then(function () { return legacyUnion(list); });
  }

  // ── Linked products (wear it with / complete the set) ─────────────────────
  var _lnk = {}; // handle -> raw { wear_it_with, complete_the_set }

  // Cached raw /complementary fetch (in-memory + localStorage).
  function fetchComplementary(handle) {
    if (_lnk[handle]) return Promise.resolve(_lnk[handle]);
    var cached = lsGet('lnk.' + handle);
    if (cached) { _lnk[handle] = cached; return Promise.resolve(cached); }
    return fetchJSON(BASE + '/products/' + encodeURIComponent(handle) + '/complementary')
      .then(function (d) {
        var raw = { wear_it_with: d.wear_it_with || [], complete_the_set: d.complete_the_set || [] };
        _lnk[handle] = raw; lsSet('lnk.' + handle, raw);
        return raw;
      })
      .catch(function () { return { wear_it_with: [], complete_the_set: [] }; });
  }

  function mapLinkedItem(it) {
    return {
      handle: it.handle,
      title: it.title,
      url: it.url || ('/products/' + it.handle),
      image_urls: it.imageUrl ? [it.imageUrl] : [],
      pricing: (it.price != null && it.price !== '') ? { min: it.price, max: it.price } : undefined
    };
  }

  function legacyColorsFromSiblings(sib) {
    function grp(arr) {
      return (arr || []).map(function (it) {
        return { variant: { handle: it.handle, color: it.color, color_code: it.hex } };
      });
    }
    return {
      core: grp(sib.core),
      le: grp(sib.limited),
      all_handles: [].concat(sib.core || [], sib.limited || []).map(function (x) { return x.handle; })
    };
  }

  function attachColors(items) {
    var handles = items.map(function (i) { return i.handle; }).filter(Boolean);
    if (!handles.length) return Promise.resolve();
    return ensureSiblings(handles).then(function () {
      items.forEach(function (i) { var sib = getSib(i.handle); if (sib) i.colors = legacyColorsFromSiblings(sib); });
    });
  }

  function enrichLinkedItem(item) {
    return fetchJSON('/products/' + item.handle + '.js').then(function (p) {
      if (!item.title) item.title = p.title;
      item.type = p.type;
      if ((!item.image_urls || !item.image_urls.length) && p.images) item.image_urls = p.images;
      item.variants = (p.variants || []).map(function (v) {
        return {
          id: v.id, title: v.title, sku: v.sku, available: v.available,
          price: v.price, options: v.options, featured_image: v.featured_image
        };
      });
      if (p.price != null) {
        item.pricing = {
          min: (p.price / 100).toFixed(2),
          max: ((p.price_max != null ? p.price_max : p.price) / 100).toFixed(2),
          currency: p.currency || (window.Shopify && window.Shopify.currency && window.Shopify.currency.active)
        };
      }
      return item;
    }).catch(function () { if (!item.variants) item.variants = []; return item; });
  }

  function linkedProducts(handleOrOpts, maybeOpts) {
    var opts = maybeOpts || (typeof handleOrOpts === 'object' ? handleOrOpts : {});
    var handle = typeof handleOrOpts === 'string' ? handleOrOpts : (opts.handle || '');
    handle = String(handle || '').trim();
    if (!handle) return Promise.resolve({ wearit: [], complete: [] });

    return fetchComplementary(handle).then(function (d) {
      var wearit = (d.wear_it_with || []).map(mapLinkedItem);
      var complete = (d.complete_the_set || []).map(mapLinkedItem);
      var all = wearit.concat(complete);
      var tasks = [];
      if (opts.withColors && all.length) tasks.push(attachColors(all));
      if (opts.enrich && all.length) tasks.push(Promise.all(all.map(enrichLinkedItem)));
      if (!tasks.length) return { wearit: wearit, complete: complete };
      return Promise.all(tasks).then(function () { return { wearit: wearit, complete: complete }; });
    });
  }

  // ── Full palette (one cheap, cached call) ────────────────────────────────
  // GET /v1/colors -> [{ slug, name, hex, edition }]. Cached in-memory +
  // localStorage (TTL). Lets surfaces that just need the colour list (e.g. the
  // mega-menu's Shop-by-Colour grid) avoid fetching every product's siblings.
  var _colors = null, _colorsAt = 0;
  function colors() {
    if (_colors && (Date.now() - _colorsAt) < TTL_MS) return Promise.resolve(_colors);
    var ls = lsGet('colors.all');
    if (ls) { _colors = ls; _colorsAt = Date.now(); return Promise.resolve(ls); }
    return fetchJSON(BASE + '/colors').then(function (d) {
      var list = (d && d.colors) || [];
      _colors = list; _colorsAt = Date.now(); lsSet('colors.all', list);
      return list;
    }).catch(function () { return _colors || []; });
  }

  // ── Full product enrichment (handle -> storefront product) ────────────────
  // Turns a product handle into the full Shopify product object (images, media,
  // options-with-values, variants, pricing) via the storefront
  // /products/{handle}.js endpoint — the SAME pattern the theme already uses
  // (see enrichLinkedItem above and the Department Section PDP). Cached
  // in-memory + localStorage (TTL) so repeat opens / revisits render instantly.
  // Lets surfaces that only receive handles (e.g. Flash Deals) hydrate cards and
  // the mini-PDP without each one re-implementing fetch+cache.
  var _prod = {};
  function product(handle) {
    var h = String(handle || '').trim();
    if (!h) return Promise.resolve(null);
    if (_prod[h]) return Promise.resolve(_prod[h]);
    var cached = lsGet('prod.' + h);
    if (cached) { _prod[h] = cached; return Promise.resolve(cached); }
    return fetchJSON('/products/' + encodeURIComponent(h) + '.js')
      .then(function (p) {
        if (p && p.handle) { _prod[h] = p; lsSet('prod.' + h, p); return p; }
        return null;
      })
      .catch(function () { return null; });
  }

  // ── Active deals / flash sales ────────────────────────────────────────────
  // GET /v1/sales -> { generatedAt, sales[], products[], byColor[] } — every
  // product on an active colour-scoped sale, enriched + grouped by colour.
  // Cached IN-MEMORY only (short TTL) — deals are time-sensitive, so we do NOT
  // persist them to localStorage. Each Flash Deals page view costs ~1 request.
  var _deals = null, _dealsAt = 0;
  var DEALS_TTL_MS = 60 * 1000;
  var EMPTY_DEALS = { sales: [], products: [], byColor: [] };
  function deals() {
    if (_deals && (Date.now() - _dealsAt) < DEALS_TTL_MS) return Promise.resolve(_deals);
    return fetchJSON(BASE + '/sales').then(function (d) {
      _deals = d || EMPTY_DEALS; _dealsAt = Date.now();
      return _deals;
    }).catch(function () { return _deals || EMPTY_DEALS; });
  }

  // ── Prefetch on hover/touch of a product link — warms the PDP ─────────────
  var _prefetched = {};
  function handleFromHref(href) {
    if (!href) return '';
    var m = String(href).match(/\/products\/([^/?#]+)/);
    return m ? m[1] : '';
  }
  function prefetch(handle) {
    if (!handle || _prefetched[handle]) return;
    _prefetched[handle] = true;
    // Siblings are usually already cached from the listing page; complementary
    // (PDP-only) is the real win — warm both, cheaply and idempotently.
    ensureSiblings([handle]);
    fetchComplementary(handle);
  }
  function wirePrefetch() {
    var hoverTimer = null;
    function onIntent(e) {
      var a = e.target && e.target.closest ? e.target.closest('a[href*="/products/"]') : null;
      if (!a) return;
      var handle = handleFromHref(a.getAttribute('href'));
      if (!handle || _prefetched[handle]) return;
      clearTimeout(hoverTimer);
      hoverTimer = setTimeout(function () { prefetch(handle); }, PREFETCH_HOVER_MS);
    }
    document.addEventListener('mouseover', onIntent, { passive: true });
    document.addEventListener('touchstart', onIntent, { passive: true });
    document.addEventListener('mouseout', function () { clearTimeout(hoverTimer); }, { passive: true });
  }
  if (document.readyState !== 'loading') wirePrefetch();
  else document.addEventListener('DOMContentLoaded', wirePrefetch);

  window.HealixColors = {
    base: BASE,
    colorProductSiblings: colorProductSiblings,
    siblingsForHandle: function (h) { return colorProductSiblings([h]); },
    linkedProducts: linkedProducts,
    product: product,
    colors: colors,
    deals: deals,
    prefetch: prefetch
  };
})();
